import Constants from 'expo-constants';
import { useCallback, useEffect, useRef, useState } from 'react';

interface DecodedData {
  data: number[] | string;
}

interface CaptureDeviceInfo {
  guid: string;
  name?: string;
  type?: number;
}

interface CaptureEvent<T> {
  id: number;
  result?: number;
  type?: number;
  value?: T;
}

interface AppInfo {
  appIdAndroid: string;
  appIdIos: string;
  appKeyAndroid: string;
  appKeyIos: string;
  developerId: string;
}

// CaptureRn interface that matches the actual class
interface SafeCaptureRn {
  close(): Promise<number>;
  open(
    appInfo: AppInfo,
    callback: (event: CaptureEvent<DecodedData | CaptureDeviceInfo>) => void,
  ): Promise<number>;
  openDevice(guid: string, parentCapture: SafeCaptureRn): Promise<number>;
}

// Attempt to import the CaptureRn since it needs to be natively linked
let CapturePkg = null;
try {
  CapturePkg = require('react-native-capture');
} catch {
  // Do nothing
}
const CaptureRn: new () => SafeCaptureRn | null = CapturePkg?.CaptureRn ?? null;

const expoConfig = Constants.expoConfig!;
const socketMobileConfig = expoConfig.extra?.socketMobile;
const androidPackageName = expoConfig.android?.package;
const iosBundleIdentifier = expoConfig.ios?.bundleIdentifier;

const appInfo =
  socketMobileConfig && androidPackageName && iosBundleIdentifier
    ? {
        appIdAndroid: `android:${androidPackageName}`,
        appIdIos: `ios:${iosBundleIdentifier}`,
        appKeyAndroid: socketMobileConfig.appKeyAndroid,
        appKeyIos: socketMobileConfig.appKeyIos,
        developerId: socketMobileConfig.developerId,
      }
    : null;

const convertAsciiArrayToString = (data: number[]): string => {
  return data.map((code) => String.fromCharCode(code)).join('');
};

export const useSocketMobileCapture = (
  submitCode: (barcode: string) => void,
) => {
  const [capture, setDeviceCapture] = useState<SafeCaptureRn | null>(
    CaptureRn ? new CaptureRn() : null,
  );
  const [isCapturing, setIsCapturing] = useState(false);
  const submitCodeRef = useRef(submitCode);

  // onCaptureEvent needs to be a stable reference for the SDK
  // so use a ref to avoid stale closures when submitting code
  useEffect(() => {
    submitCodeRef.current = submitCode;
  }, [submitCode]);

  const onCaptureEvent = useCallback(
    async (e: CaptureEvent<DecodedData | CaptureDeviceInfo>) => {
      if (!e) {
        return;
      }

      switch (e.id) {
        case 1: // CaptureEventIds.DeviceArrival
          if (capture) {
            try {
              const guid = (e as CaptureEvent<CaptureDeviceInfo>).value?.guid;
              if (guid) {
                const newDevice = new CaptureRn();
                await newDevice!.openDevice(guid, capture);
                setDeviceCapture(newDevice);
              }
            } catch {
              // Do nothing
            }
          }
          break;
        case 2: // CaptureEventIds.DeviceRemoval
          if (capture) {
            try {
              await capture.close();
              setDeviceCapture(new CaptureRn());
              setIsCapturing(false);
            } catch {
              // Do nothing
            }
          }
          break;
        case 5: // CaptureEventIds.DecodedData
          const decodedData = (e as CaptureEvent<DecodedData>).value?.data;
          if (decodedData) {
            let barcodeString = '';
            if (Array.isArray(decodedData)) {
              barcodeString = convertAsciiArrayToString(decodedData);
            } else if (typeof decodedData === 'string') {
              barcodeString = decodedData;
            }

            if (barcodeString) {
              submitCodeRef.current(barcodeString);
            }
          }
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    const openCapture = async () => {
      if (!capture || !appInfo || isCapturing) {
        return;
      }

      try {
        await capture?.open(appInfo, onCaptureEvent);
        setIsCapturing(true);
      } catch (e) {
        // Do nothing
      }
    };

    openCapture();
  }, [isCapturing, capture, onCaptureEvent]);
};
